import {
  CalendarClock,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Sun,
  Moon,
  Sparkles,
  Check,
} from "lucide-react";
import { useEffect, useState } from "react";

export const ScheduleModal = ({ isOpen, onClose, onConfirm, initialDate }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [isAM, setIsAM] = useState(true);

  useEffect(() => {
    const now = new Date();

    const init = initialDate
      ? typeof initialDate === "string"
        ? new Date(initialDate)
        : initialDate
      : now;

    const date = new Date(init);

    // Ensure date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid initial date provided, using current time");
      setSelectedDate(now);
      setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    } else {
      setSelectedDate(date);
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }

    // Fix time extraction logic
    let hours = date.getHours();
    const minutes = date.getMinutes();

    // Determine AM/PM first
    const ampm = hours < 12;

    // Convert to 12-hour format
    let displayHours = hours;
    if (hours === 0) {
      displayHours = 12; // Midnight is 12 AM
    } else if (hours > 12) {
      displayHours = hours - 12; // PM hours
    }
    // Note: 12 PM (noon) stays as 12

    setSelectedHour(displayHours);
    setSelectedMinute(Math.round(minutes / 5) * 5); // Round to nearest 5 minutes
    setIsAM(ampm);
  }, [initialDate, isOpen]);

  if (!isOpen) return null;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const selectDate = (date) => {
    if (date && !isPastDate(date)) {
      setSelectedDate(date);
    }
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const handleConfirm = () => {
    if (selectedDate) {
      const finalDate = new Date(selectedDate);
      let hours = selectedHour;

      // Convert from 12-hour to 24-hour format correctly
      if (isAM) {
        if (hours === 12) {
          hours = 0; // 12 AM = 0 hours
        }
        // Other AM hours (1-11) stay the same
      } else {
        if (hours !== 12) {
          hours += 12; // PM hours except 12 PM
        }
        // 12 PM stays as 12 hours
      }

      finalDate.setHours(hours, selectedMinute, 0, 0);
      onConfirm(finalDate);
    }
  };

  const formatSelectedDate = () => {
    if (!selectedDate) return { date: "", time: "" };

    const options = {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    };

    const date = selectedDate.toLocaleDateString("en-US", options);
    const timeStr = `${selectedHour}:${selectedMinute
      .toString()
      .padStart(2, "0")} ${isAM ? "AM" : "PM"}`;

    return { date, time: timeStr };
  };

  const days = getDaysInMonth(currentMonth);
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  const selectedDateTime = formatSelectedDate();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col relative max-h-[90vh] overflow-hidden transform animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CalendarClock className="text-white" size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Schedule</h2>
                <p className="text-blue-100 text-xs">Pick date & time</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition p-1.5 rounded-lg hover:bg-white/20"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Selected Date & Time Display */}
          {selectedDate && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Check className="text-green-600" size={16} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wide">
                      Selected
                    </p>
                    <div className="flex items-center gap-4 mt-1">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-green-700" />
                        <span className="font-semibold text-green-800 text-sm">
                          {selectedDateTime.date}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-green-700" />
                        <span className="font-semibold text-green-800 text-sm">
                          {selectedDateTime.time}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Date and Time Selectors */}
          <div className="grid grid-cols-2 gap-4">
            {/* Date Selector */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="text-blue-600" size={14} />
                <h3 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                  Date
                </h3>
              </div>

              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-3 bg-white rounded-md p-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-1 hover:bg-gray-100 rounded text-gray-600"
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="text-center">
                  <h3 className="font-medium text-gray-800 text-xs">
                    {monthNames[currentMonth.getMonth()].slice(0, 3)}{" "}
                    {currentMonth.getFullYear()}
                  </h3>
                </div>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-1 hover:bg-gray-100 rounded text-gray-600"
                >
                  <ChevronRight size={14} />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="bg-white rounded-md p-2">
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {daysOfWeek.map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-medium text-gray-400 py-1"
                    >
                      {day.slice(0, 1)}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0.5">
                  {days.map((date, index) => (
                    <button
                      key={index}
                      onClick={() => date && selectDate(date)}
                      disabled={!date || isPastDate(date)}
                      className={`
                        h-6 w-full rounded text-xs font-medium transition-all duration-150 relative
                        ${!date ? "invisible" : ""}
                        ${
                          date && isPastDate(date)
                            ? "text-gray-300 cursor-not-allowed"
                            : "hover:bg-blue-50 cursor-pointer"
                        }
                        ${
                          date && selectedDate && isSameDay(date, selectedDate)
                            ? "bg-blue-600 text-white shadow-sm"
                            : date && isToday(date)
                            ? "bg-blue-100 text-blue-700 border border-blue-300"
                            : "text-gray-700 hover:text-blue-600"
                        }
                      `}
                    >
                      {date?.getDate()}
                      {date && isToday(date) && (
                        <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Time Selector */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="text-indigo-600" size={14} />
                <h3 className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                  Time
                </h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Hour
                  </label>
                  <select
                    value={selectedHour}
                    onChange={(e) => setSelectedHour(parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-all bg-white"
                  >
                    {hours.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Minute
                  </label>
                  <select
                    value={selectedMinute}
                    onChange={(e) =>
                      setSelectedMinute(parseInt(e.target.value))
                    }
                    className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-all bg-white"
                  >
                    {minutes.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute.toString().padStart(2, "0")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Period
                  </label>
                  <div className="flex rounded-md bg-gray-200 p-0.5">
                    <button
                      onClick={() => setIsAM(true)}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-medium transition-all ${
                        isAM
                          ? "bg-white text-amber-600 shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      <Sun size={10} />
                      AM
                    </button>
                    <button
                      onClick={() => setIsAM(false)}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-medium transition-all ${
                        !isAM
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-gray-500"
                      }`}
                    >
                      <Moon size={10} />
                      PM
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 bg-gray-50 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 px-4 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 text-sm font-medium transition-all shadow-sm flex items-center justify-center gap-1.5"
          >
            <Sparkles size={14} />
            Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
